import { Box, Typography } from '@mui/material';
import { type AxisKey } from '~/utils/units';

/** One axis component of a vector value. */
export interface AxisValue {
    readonly axis: AxisKey;
    /** Formatted value in the row's display unit; no unit suffix. */
    readonly value: string;
}

/**
 * Renders a vector value as per-axis (X/Y/Z) components so the coordinate
 * frame is explicit, with the shared `unit` shown once after the last number
 * (so the unit sits on the value, like every scalar row). Each axis letter, in
 * the turquoise accent, stands right before its own number, and the components
 * stand a wider gap apart, so each letter reads with its number. Every number
 * but the last has a fixed width, so rows of vectors line up. With `wrap`, a
 * value too wide for its line moves whole components to the next line; it
 * is never cut short.
 */
export default function AxisTripletValue({
    axes,
    unit,
    wrap = false,
    'data-testid': testId,
}: {
    axes: readonly AxisValue[];
    unit: string;
    wrap?: boolean;
    'data-testid'?: string;
}) {
    // A degree sign sits on its number; any other unit stands a space apart.
    const unitSuffix = unit === '°' ? unit : ` ${unit}`;
    // The text a scalar value is shown in, so every row is as tall.
    return (
        <Typography
            component="span"
            fontSize={12}
            lineHeight={1.2}
            display="inline-flex"
            alignItems="baseline"
            flexWrap={wrap ? 'wrap' : undefined}
            columnGap={0.5}
            minWidth={0}
            whiteSpace="nowrap"
            data-testid={testId}
        >
            {axes.map((component, index) => (
                <Box
                    key={component.axis}
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        fontSize: 12,
                    }}
                >
                    {/* All axis letters share the turquoise accent; the
                            value stays in the default high-contrast text color
                            so the clinical number is easy to read. */}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 400,
                            mr: 0.25,
                            color: 'primary.main',
                        }}
                    >
                        {component.axis.toUpperCase()}
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            minWidth:
                                index < axes.length - 1 ? '5ch' : undefined,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {component.value}
                        {index === axes.length - 1 && unitSuffix}
                    </Box>
                </Box>
            ))}
        </Typography>
    );
}
